import { Injectable } from '@angular/core';
import { DialogService, IDialogOptions } from '@tylertech/forge-angular';
import { environment } from 'src/environments/environment';
import { CaptchaDialogComponent } from 'src/app/components/captcha/captcha-dialog.component';

// Cloudflare Turnstile client SDK, loaded lazily with explicit rendering so we
// control exactly when the widget mounts inside the dialog.
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Presents the Cloudflare Turnstile challenge to the user and resolves the
 * resulting token.
 *
 * The Turnstile SDK is loaded lazily (only when a challenge is actually
 * required). The token it returns is sent on subsequent API calls (see
 * RateLimitInterceptor) so the backend can verify the solved challenge
 * server-side against Cloudflare's siteverify endpoint.
 */
@Injectable({
  providedIn: 'root'
})
export class CaptchaService {
  private _scriptPromise: Promise<void> | null = null;
  // Guards against opening multiple dialogs when several requests fail at once.
  private _inFlight: Promise<string | null> | null = null;

  constructor(private readonly _dialogService: DialogService) {}

  private get _isConfigured(): boolean {
    const cfg = environment.turnstile;
    return !!cfg?.siteKey && !cfg.siteKey.startsWith('REPLACE_WITH');
  }

  /**
   * Loads the Turnstile SDK once and caches the promise. Rejects if the script
   * fails to load so callers can fall back gracefully.
   */
  private loadSdk(): Promise<void> {
    if (this._scriptPromise) {
      return this._scriptPromise;
    }

    this._scriptPromise = new Promise<void>((resolve, reject) => {
      if (window.turnstile) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        this._scriptPromise = null; // allow a later retry
        reject(new Error('Failed to load Cloudflare Turnstile SDK'));
      };
      document.head.appendChild(script);
    });

    return this._scriptPromise;
  }

  /**
   * Shows the challenge dialog and resolves with the solved token, or null if
   * the user cancelled, the SDK is unconfigured, or loading failed. Concurrent
   * calls share a single dialog/promise.
   */
  solve(): Promise<string | null> {
    if (!this._isConfigured) {
      return Promise.resolve(null);
    }

    if (this._inFlight) {
      return this._inFlight;
    }

    this._inFlight = this.loadSdk()
      .then(() => this.openDialog())
      .catch(() => null)
      .finally(() => { this._inFlight = null; });

    return this._inFlight;
  }

  private openDialog(): Promise<string | null> {
    const options: IDialogOptions = {
      persistent: true,
      attributes: new Map([
        ['label', "Verify you're human"],
        ['role', 'dialog']
      ])
    };

    const dialogRef = this._dialogService.open<CaptchaDialogComponent, string | null>(
      CaptchaDialogComponent,
      { options }
    );

    return new Promise<string | null>((resolve) => {
      dialogRef.afterClosed.subscribe((token: string | null | undefined) => resolve(token ?? null));
    });
  }
}